// src/pages/StageAnalyticsPage.js
import { ChartBarIcon, LockClosedIcon } from "@heroicons/react/24/outline";
import { doc, getDoc } from "firebase/firestore";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { fetchVideoAnalytics, exportVideoAnalyticsCsv, exportVideoAnalyticsReport } from "../analytics/api/adminAnalytics";
import AnalyticsEmptyState from "../analytics/components/AnalyticsEmptyState";
import ConcurrentChart from "../analytics/components/ConcurrentChart";
import CountryBreakdownTable from "../analytics/components/CountryBreakdownTable";
import SessionLogTable from "../analytics/components/SessionLogTable";
import SummaryCards from "../analytics/components/SummaryCards";
import ViewsOverTimeChart from "../analytics/components/ViewsOverTimeChart";
import WatchDistributionChart from "../analytics/components/WatchDistributionChart";
import { useAuth } from "../common/contexts/AuthContext";
import { firestore } from "../firebase";

// 30s poll. The backend serves a cached rollup and recomputes at most once a minute per page (ADR-003),
// so polling faster would only show the same numbers while still paying for the auth reads.
const ANALYTICS_POLL_MS = 30000;

const PLAYBACK_SIGNAL_NOTES = {
  interaction:
    "This page uses an embed that does not report play/pause. Watch views and watch time are estimated: a play is counted when a viewer clicks into the player.",
  vimeo_api:
    "Play/pause comes from the Vimeo player's own events. Viewers on the page are counted even before the stream starts.",
};

function downloadReportFile({ filename, html }) {
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename || "video-analytics-report.html";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

const EMPTY_ANALYTICS = {
  total_page_views: 0,
  total_watch_views: 0,
  unique_page_viewers: 0,
  unique_watch_viewers: 0,
  unique_player_viewers: 0,
  session_duration_seconds: 0,
  watch_duration_seconds: 0,
  median_watch_duration_seconds: 0,
  peak_concurrent: 0,
  peak_concurrent_at: null,
  peak_watching: 0,
  current_viewers: 0,
  current_watching: 0,
  concurrent_bucket_minutes: 1,
  concurrent_over_time: [],
  views_over_time: [],
  watch_time_distribution: [
    { label: "0-25%", count: 0 },
    { label: "25-50%", count: 0 },
    { label: "50-75%", count: 0 },
    { label: "75-100%", count: 0 },
  ],
  country_breakdown: [],
  session_log: [],
  mode: "live",
  last_updated_at: null,
  stream_duration_seconds: 5,
  stream_duration_estimated: true,
};

const hasAdminAccess = ({ accountData, currentUser, event }) => {
  const isListedAdmin = accountData?.admins?.includes(currentUser?.email);
  return Boolean(
    event?.user_id === currentUser?.uid &&
      event?.account_id === accountData?.id &&
      isListedAdmin
  );
};

function AccessState({ title, message }) {
  return (
    <div className="min-h-screen bg-slate-50 px-5 py-10 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-3xl rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-start gap-4">
          <div className="grid h-12 w-12 flex-none place-items-center rounded-lg bg-slate-100 text-slate-600">
            <LockClosedIcon className="h-6 w-6" aria-hidden="true" />
          </div>
          <div>
            <h1 className="text-xl font-semibold tracking-tight text-slate-950">
              {title}
            </h1>
            <p className="mt-2 text-sm leading-6 text-slate-600">{message}</p>
            <Link to="/dashboard" className="secondary-button mt-5 inline-flex">
              Back to dashboard
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function downloadCsvFile({ filename, csv }) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename || "video-analytics.csv";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export default function StageAnalyticsPage() {
  const { eventId } = useParams();
  const { currentUser, accountData } = useAuth();
  const [event, setEvent] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [analyticsError, setAnalyticsError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isAnalyticsLoading, setIsAnalyticsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isReportExporting, setIsReportExporting] = useState(false);
  const isAnalyticsFetchInFlightRef = useRef(false);

  useEffect(() => {
    let isMounted = true;

    const loadEvent = async () => {
      setIsLoading(true);
      setErrorMessage("");
      setAnalyticsError("");
      setEvent(null);
      setAnalytics(null);

      if (!eventId) {
        console.warn(
          "ANALYTICS_ACCESS_DENIED",
          `reason=missing_event_id user_id=${currentUser?.uid || "unknown"}`
        );
        if (isMounted) {
          setErrorMessage("Analytics page not found.");
          setIsLoading(false);
        }
        return;
      }

      try {
        const eventSnapshot = await getDoc(doc(firestore, "events", eventId));

        if (!eventSnapshot.exists()) {
          console.warn(
            "ANALYTICS_ACCESS_DENIED",
            `reason=event_not_found event_id=${eventId} user_id=${currentUser?.uid || "unknown"}`
          );
          if (isMounted) {
            setErrorMessage("Analytics page not found.");
            setIsLoading(false);
          }
          return;
        }

        const eventData = { ...eventSnapshot.data(), id: eventSnapshot.id };

        if (!hasAdminAccess({ accountData, currentUser, event: eventData })) {
          console.warn(
            "ANALYTICS_ACCESS_DENIED",
            `reason=admin_required event_id=${eventId} user_id=${currentUser?.uid || "unknown"}`
          );
          if (isMounted) {
            setErrorMessage("You need admin access to this page to view analytics.");
            setIsLoading(false);
          }
          return;
        }

        console.info(
          "ANALYTICS_ACCESS_GRANTED",
          `event_id=${eventId} user_id=${currentUser.uid}`
        );

        if (isMounted) {
          setEvent(eventData);
          setIsLoading(false);
        }
      } catch (error) {
        const errorCode = error?.code || "unknown";
        const normalizedMessage = String(error?.message || error).replace(/\s+/g, "_");
        const permissionDenied = errorCode === "permission-denied";
        console.error(
          "ANALYTICS_PAGE_LOAD_FAILED",
          `event_id=${eventId} user_id=${currentUser?.uid || "unknown"} code=${errorCode} message=${normalizedMessage}`
        );
        if (isMounted) {
          setErrorMessage(
            permissionDenied
              ? "You need admin access to this page to view analytics."
              : "Analytics could not be loaded. Refresh and try again."
          );
          setIsLoading(false);
        }
      }
    };

    loadEvent();

    return () => {
      isMounted = false;
    };
  }, [accountData, currentUser, eventId]);

  const loadAnalytics = useCallback(async () => {
    if (!event?.id || isAnalyticsFetchInFlightRef.current) return;

    isAnalyticsFetchInFlightRef.current = true;
    setIsAnalyticsLoading(true);
    setAnalyticsError("");
    try {
      const data = await fetchVideoAnalytics(event.id);
      console.info("ANALYTICS_DASHBOARD_REFRESHED", `event_id=${event.id} mode=${data?.mode || "unknown"}`);
      setAnalytics(data);
    } catch (error) {
      console.error("ANALYTICS_DASHBOARD_LOAD_FAILED", `event_id=${event.id} code=${error?.code || "unknown"}`);
      setAnalyticsError(error?.message || "Analytics could not be loaded. Refresh and try again.");
    } finally {
      isAnalyticsFetchInFlightRef.current = false;
      setIsAnalyticsLoading(false);
    }
  }, [event?.id]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  useEffect(() => {
    if (!event?.id) return undefined;

    const refreshIfVisible = () => {
      if (document.visibilityState !== "hidden") loadAnalytics();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") loadAnalytics();
    };

    const intervalId = window.setInterval(refreshIfVisible, ANALYTICS_POLL_MS);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [event?.id, loadAnalytics]);

  const handleExportCsv = async () => {
    if (!event?.id) return;
    setIsExporting(true);
    try {
      const result = await exportVideoAnalyticsCsv(event.id);
      downloadCsvFile(result);
    } catch (error) {
      setAnalyticsError(error?.message || "CSV export failed. Try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportReport = async () => {
    if (!event?.id) return;
    setIsReportExporting(true);
    try {
      const result = await exportVideoAnalyticsReport(event.id);
      downloadReportFile(result);
    } catch (error) {
      setAnalyticsError(error?.message || "Report export failed. Try again.");
    } finally {
      setIsReportExporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 px-5 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="h-6 w-48 animate-pulse rounded bg-slate-100" />
          <div className="mt-4 h-10 w-72 animate-pulse rounded bg-slate-100" />
        </div>
      </div>
    );
  }

  if (errorMessage) {
    return <AccessState title="Analytics unavailable" message={errorMessage} />;
  }

  const displayedAnalytics = analytics || EMPTY_ANALYTICS;
  const hasViews = displayedAnalytics.total_page_views > 0;

  return (
    <div className="min-h-screen bg-slate-50 px-5 py-10 sm:px-6 lg:px-8">
      <main className="mx-auto max-w-7xl">
        <Link to="/dashboard" className="text-sm font-semibold text-slate-600 hover:text-slate-950">
          ← Back to dashboard
        </Link>

        <section className="mt-6 rounded-xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-start gap-4">
              <div className="grid h-12 w-12 flex-none place-items-center rounded-lg bg-[#FF3A20]/10 text-[#FF3A20]">
                <ChartBarIcon className="h-6 w-6" aria-hidden="true" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Analytics</p>
                <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-950">
                  Analytics for {event?.event_name || "this page"}
                </h1>
                <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600">
                  Page views, watch views, concurrent viewers, watch depth, locations, and session-level export.
                </p>
              </div>
            </div>
            {analytics?.last_updated_at && (
              <div className="rounded-lg bg-slate-50 px-4 py-3 text-sm text-slate-600">
                <span className="font-semibold text-slate-900">{analytics.mode === "live" ? "Live" : "Post-event"}</span>
                <br />Last updated {new Date(analytics.last_updated_at).toLocaleTimeString()}
              </div>
            )}
          </div>
        </section>

        {isAnalyticsLoading && !analytics && (
          <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-600 shadow-sm">
            Loading analytics…
          </div>
        )}

        {analyticsError && (
          <div className="mt-6 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
            {analyticsError}
          </div>
        )}

        {event && (
          <div className="mt-6 space-y-6">
            <SummaryCards analytics={displayedAnalytics} />

            {PLAYBACK_SIGNAL_NOTES[displayedAnalytics.playback_signal] && (
              <p className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600" data-testid="playback-signal-note">
                {PLAYBACK_SIGNAL_NOTES[displayedAnalytics.playback_signal]}
              </p>
            )}

            {!hasViews && !isAnalyticsLoading && (
              <AnalyticsEmptyState
                title="No viewer sessions recorded yet"
                message="The full analytics dashboard is ready. These fields will populate automatically after public viewers open this page and play the video."
              />
            )}

            <div className="grid gap-6">
              <ConcurrentChart
                data={displayedAnalytics.concurrent_over_time}
                peak={displayedAnalytics.peak_concurrent}
                peakWatching={displayedAnalytics.peak_watching}
                current={displayedAnalytics.current_viewers}
                currentWatching={displayedAnalytics.current_watching}
                bucketMinutes={displayedAnalytics.concurrent_bucket_minutes}
              />
              <ViewsOverTimeChart data={displayedAnalytics.views_over_time} />
            </div>
            <div className="grid gap-6 lg:grid-cols-2">
              <WatchDistributionChart data={displayedAnalytics.watch_time_distribution} />
              <CountryBreakdownTable rows={displayedAnalytics.country_breakdown} />
            </div>
            <SessionLogTable
              rows={displayedAnalytics.session_log}
              totalRows={displayedAnalytics.session_log_total}
              onExportCsv={handleExportCsv}
              onExportReport={handleExportReport}
              isExporting={isExporting}
              isReportExporting={isReportExporting}
            />
          </div>
        )}
      </main>
    </div>
  );
}

export { ANALYTICS_POLL_MS, hasAdminAccess };
